import { hitToProductNode } from "@repo/shopify/algolia/adapter";
import { toAlgoliaClickTarget, withAlgoliaClick } from "@repo/shopify/algolia/insights";
import type { IAlgoliaProductHit } from "@repo/shopify/types/api/algolia";
import type { ISearchEdge } from "@repo/shopify/types/api/search";
import type { Product } from "@repo/shopify/types/generated/storefront.types";

/**
 * Algolia 변형(variant) 레코드를 thisisneverthat 화면이 쓰는 상품/색상 카드 모양으로 조립한다.
 *
 * thisisneverthat 인덱스는 distinct 없이 변형마다 레코드가 온다(실측: hoodie 200건 → 상품 52개).
 * 화면이 색상별 카드를 만들려면 한 상품의 변형이 모두 있어야 해서, 여기서 상품 단위로 묶는다.
 * (tune / khakis 는 인덱스 distinct 로 한 건 = 한 상품이라 묶지 않는다.)
 */

/** 목록을 키의 첫 등장 순서를 지키며 묶는다. 첫 등장 순서라 Algolia 랭킹 순서가 유지된다. */
function groupBy<T>(list: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  list.forEach((item) => {
    const key = keyOf(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });

  return groups;
}

/**
 * 같은 상품에 속한 변형 목록 → Shopify edge.
 * position 은 화면에 보이는 카드 순서(1부터)로, 검색 결과 클릭 이벤트에 쓴다.
 */
function toProductEdge(variants: IAlgoliaProductHit[], colors: string[], position: number): ISearchEdge {
  const node = hitToProductNode(variants[0]) as Product;
  const sizes = [...new Set(variants.map((variant) => variant.options?.size).filter(Boolean))] as string[];
  const edges = variants.map((hit) => {
    return {
      node: {
        id: `gid://shopify/ProductVariant/${hit.objectID}`,
        title: hit.variant_title ?? "",
        sku: hit.sku ?? "",
        availableForSale: hit.inventory_available ?? true,
        image: { url: hit.image ?? hit.product_image ?? "" },
        product: { handle: hit.handle },
        price: { amount: String(hit.price ?? 0), currencyCode: "KRW" },
        compareAtPrice: hit.compare_at_price ? { amount: String(hit.compare_at_price), currencyCode: "KRW" } : null,
        selectedOptions: Object.entries(hit.options ?? {}).map(([name, value]) => ({
          // Shopify 는 "Color" / "Size", Algolia 는 소문자 키라 화면 로직에 맞춰 첫 글자를 올린다.
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value,
        })),
      },
    };
  });

  return {
    node: withAlgoliaClick(
      {
        ...node,
        // 화면이 options(Color) 와 variants.selectedOptions 로 색상 카드를 구성한다.
        options: [
          { id: `${node.handle}-color`, name: "Color", values: colors },
          { id: `${node.handle}-size`, name: "Size", values: sizes },
        ],
        // 아카이브 목록은 품절 카드를 숨기는 데 이 값을 쓴다(useCollections 의 getExpandList 와 동일).
        availableForSale: edges.every(({ node: variant }) => variant.availableForSale),
        variants: { edges },
      },
      toAlgoliaClickTarget(variants[0], position),
    ),
  } as ISearchEdge;
}

/**
 * hidden 상품을 걸러 내고 상품(handle) 단위로 묶는다.
 * hidden 은 인덱스의 facet 이 아니라 filters 로 못 걸러서 기존 화면과 동일하게 여기서 처리한다.
 */
export function toProductGroups(items: unknown[]): IAlgoliaProductHit[][] {
  const visible = (items as IAlgoliaProductHit[]).filter((hit) => hit.meta?.operation?.hidden !== "true");

  // 변형은 Shopify 와 같은 순서(position)로 둔다. 카드의 색상칩 순서와 대표 변형이 이 순서를 따른다.
  return [...groupBy(visible, (hit) => String(hit.handle || hit.id || hit.objectID)).values()].map((variants) =>
    [...variants].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  );
}

/**
 * 상품 그룹 → 상품 카드 / 색상 카드.
 * 색상 카드 순서는 기존 getExpandList 와 같게 상품순 → 색상순을 유지한다.
 */
export function toProductCards(groups: IAlgoliaProductHit[][]) {
  const products: ISearchEdge[] = [];
  const expandProducts: ISearchEdge[] = [];

  groups.forEach((variants) => {
    const byColor = groupBy(variants, (hit) => hit.options?.color ?? "");

    products.push(toProductEdge(variants, [...byColor.keys()].filter(Boolean), products.length + 1));
    byColor.forEach((colorVariants, color) => {
      // 기존 getExpandList 와 동일하게 색상 옵션이 없는 상품은 색상 카드를 만들지 않는다.
      if (color) expandProducts.push(toProductEdge(colorVariants, [color], expandProducts.length + 1));
    });
  });

  return { products, expandProducts };
}
